import { consoleRoutes } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { AppLink, ErrorState, EmptyState, Icon, Progress, Skeleton, StatCard } from "@sagot/ui";
import { usePermissions } from "../../../session/permissions";
import { useWorkspaceContext } from "../../../session/workspace-context";
import { useFormatters } from "../shared/format";
import { isPlanGated } from "../shared/plan-gate";
import { percentDelta, type AnalyticsFilters } from "./analytics-model";
import { ChartCard } from "./chart-card";
import { useAutomation } from "./use-analytics";
import type { AutomationStats } from "../workflows/p4-types";

const A = "engage.analytics.automation";
const KPI_COUNT = 8;
const PERCENT = 100;

function Kpis({ data }: { data: AutomationStats }) {
  const { t } = useTranslation();
  const fmt = useFormatters();
  return (
    <section className="grid grid-cols-2 gap-4 md:grid-cols-4" aria-label={t(`${A}.title`)} data-testid="automation-kpis">
      <StatCard label={t(`${A}.runs`)} icon="workflow" value={fmt.number(data.runs)} delta={percentDelta(data.runs_delta_pct, (value) => t("engage.analytics.kpi.vsPrevious", { value }))} footnote={t(`${A}.facingRuns`, { count: data.facing_runs, formatted: fmt.number(data.facing_runs) })} />
      <StatCard label={t(`${A}.completion`)} icon="check-circle" value={fmt.percent(data.completion_rate)} footnote={t(`${A}.completedOf`, { completed: fmt.number(data.completed), runs: fmt.number(data.runs) })} />
      <StatCard label={t(`${A}.engaged`)} icon="message-circle" value={fmt.percent(data.engaged_rate)} footnote={t(`${A}.engagedCount`, { count: data.engaged, formatted: fmt.number(data.engaged) })} />
      <StatCard label={t(`${A}.resolved`)} icon="shield-check" value={fmt.number(data.resolved_in_workflows)} footnote={t(`${A}.touched`, { count: data.conversations_touched, formatted: fmt.number(data.conversations_touched) })} />
      <StatCard label={t(`${A}.bookings`)} icon="calendar" value={fmt.number(data.assisted_bookings)} footnote={t(`${A}.assistedHint`)} />
      <StatCard label={t(`${A}.leads`)} icon="user-plus" value={fmt.number(data.assisted_leads)} footnote={t(`${A}.assistedHint`)} />
      <StatCard label={t(`${A}.goal`)} icon="target" value={fmt.percent(data.goal_rate)} footnote={t(`${A}.goalOf`, { met: fmt.number(data.goal_met), tracked: fmt.number(data.goal_tracked) })} />
      <StatCard label={t(`${A}.failed`)} icon="alert-triangle" value={fmt.number(data.failed)} footnote={t(`${A}.failedHint`)} />
    </section>
  );
}

function HomeCard({ query }: { query: ReturnType<typeof useAutomation> }) {
  const { t } = useTranslation();
  const fmt = useFormatters();
  return (
    <ChartCard
      testId="automation-home"
      title={t(`${A}.home.title`)}
      subtitle={t(`${A}.home.subtitle`)}
      query={query}
      isEmpty={({ home }) => home.impressions === 0}
      empty={{ icon: "home", title: t(`${A}.home.emptyTitle`), body: t(`${A}.home.emptyBody`) }}
      skeleton={<Skeleton className="h-24" />}
      bodyClassName="space-y-4"
    >
      {({ home }) => (
        <>
          <dl className="grid grid-cols-3 gap-3 text-center">
            <div className="rounded-md bg-surface-2 p-3"><dt className="text-[11px] font-semibold text-fg-subtle">{t(`${A}.home.impressions`)}</dt><dd className="mt-0.5 text-xl font-extrabold tabular-nums">{fmt.number(home.impressions)}</dd></div>
            <div className="rounded-md bg-surface-2 p-3"><dt className="text-[11px] font-semibold text-fg-subtle">{t(`${A}.home.taps`)}</dt><dd className="mt-0.5 text-xl font-extrabold tabular-nums">{fmt.number(home.taps)}</dd></div>
            <div className="rounded-md bg-surface-2 p-3"><dt className="text-[11px] font-semibold text-fg-subtle">{t(`${A}.home.conversions`)}</dt><dd className="mt-0.5 text-xl font-extrabold tabular-nums">{fmt.number(home.conversions)}</dd></div>
          </dl>
          <div>
            <div className="mb-1 flex justify-between text-xs"><span>{t(`${A}.home.conversion`)}</span><span className="font-semibold tabular-nums">{fmt.percent(home.conversion_rate, 1)}</span></div>
            <Progress size="sm" value={(home.conversion_rate ?? 0) * PERCENT} label={t(`${A}.home.conversion`)} valueText={fmt.percent(home.conversion_rate, 1)} />
          </div>
          <p className="text-xs text-fg-subtle">{t(`${A}.home.note`)}</p>
        </>
      )}
    </ChartCard>
  );
}

function TopWorkflowsCard({ query }: { query: ReturnType<typeof useAutomation> }) {
  const { t } = useTranslation();
  const fmt = useFormatters();
  const { summary } = useWorkspaceContext();
  return (
    <ChartCard
      testId="automation-top"
      className="xl:col-span-2"
      title={t(`${A}.top.title`)}
      query={query}
      flush
      isEmpty={({ top_workflows: top }) => top.length === 0}
      empty={{ icon: "workflow", title: t(`${A}.top.emptyTitle`), body: t(`${A}.top.emptyBody`) }}
      skeleton={<div className="space-y-2 p-4"><Skeleton className="h-5" /><Skeleton className="h-5" /><Skeleton className="h-5" /></div>}
    >
      {({ top_workflows: top }) => (
        // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex -- focusable so keyboard users can scroll it (axe scrollable-region-focusable)
        <div className="overflow-x-auto focus-visible:outline-2 focus-visible:outline-primary" role="region" aria-label={t(`${A}.top.title`)} tabIndex={0}>
          <table className="table">
            <thead>
              <tr>
                <th>{t(`${A}.top.workflow`)}</th>
                <th className="text-right">{t(`${A}.runs`)}</th>
                <th className="text-right">{t(`${A}.engaged`)}</th>
                <th className="text-right">{t(`${A}.top.completed`)}</th>
                <th className="text-right">{t(`${A}.goal`)}</th>
              </tr>
            </thead>
            <tbody>
              {top.map((row) => (
                <tr key={row.workflow_id}>
                  <td><AppLink className="font-semibold text-primary-ink hover:underline" href={consoleRoutes.workflow(summary.slug, row.workflow_id, "overview")}>{row.name}</AppLink></td>
                  <td className="text-right tabular-nums">{fmt.number(row.runs)}</td>
                  <td className="text-right tabular-nums">{fmt.number(row.engaged)}</td>
                  <td className="text-right tabular-nums">{fmt.number(row.completed)}</td>
                  <td className="text-right tabular-nums">{fmt.number(row.goal_met)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </ChartCard>
  );
}

/** Analytics → Automation: what workflows did in the period (runs, completion, engagement, bookings and leads they led to, goal, Home conversion). */
export function AutomationSection({ filters }: { filters: AnalyticsFilters }) {
  const { t } = useTranslation();
  const { summary } = useWorkspaceContext();
  const permissions = usePermissions();
  const query = useAutomation(filters);
  // A plan without Workflows answers 402: the section is left out, the rest of the page is unaffected.
  if (!permissions.can("workflows.view", { anywhere: true }) || (query.isError && isPlanGated(query.error))) return null;
  return (
    <section className="space-y-4" aria-labelledby="automation-heading" data-testid="analytics-automation">
      <div className="flex flex-wrap items-end justify-between gap-2">
        <div>
          <h2 id="automation-heading" className="flex items-center gap-2 text-lg font-bold">
            <Icon name="workflow" className="size-5 text-primary-ink" />
            {t(`${A}.title`)}
          </h2>
          <p className="text-xs text-fg-subtle">{t(`${A}.subtitle`)}</p>
        </div>
        <AppLink href={consoleRoutes.workflows(summary.slug)} className="text-xs font-semibold text-primary-ink hover:underline">{t(`${A}.open`)}</AppLink>
      </div>
      {query.isError ? (
        <div className="card"><ErrorState title={t("engage.analytics.loadError")} retryLabel={t("common.retry")} onRetry={() => void query.refetch()} /></div>
      ) : query.data ? (
        query.data.runs === 0 && query.data.home.impressions === 0 ? (
          <div className="card"><EmptyState icon="workflow" headingLevel={3} title={t(`${A}.emptyTitle`)} description={t(`${A}.emptyBody`)} /></div>
        ) : (
          <>
            <Kpis data={query.data} />
            <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
              <HomeCard query={query} />
              <TopWorkflowsCard query={query} />
            </div>
          </>
        )
      ) : (
        <section className="grid grid-cols-2 gap-4 md:grid-cols-4" aria-hidden="true" aria-busy="true">
          {Array.from({ length: KPI_COUNT }, (_, i) => (
            <div key={i} className="card card-body space-y-2"><Skeleton className="h-3 w-24" /><Skeleton className="h-7 w-20" /><Skeleton className="h-3 w-28" /></div>
          ))}
        </section>
      )}
    </section>
  );
}
